import moment from "moment";
import {
  RESOURCE_BREAK,
  RESOURCE_COMPASSIONATE_LEAVE,
  RESOURCE_HOLIDAY,
  RESOURCE_SICK,
  RESOURCE_WORK_HOURS
} from "@/helpers/eventTypes";
import {
  ROSTER_DAYS,
  buildRosterEventsForDate,
  isValidRosterDate,
  isValidRosterTime,
  weekdayKeyFromIsoDate
} from "@/helpers/rosterDefaults";
import {
  clippedTimeLabels,
  coerceEventMoment,
  eventOccupiedDates,
  eventOccupiesDate,
  eventStartDate,
  isMultiDayEvent,
  minutesOnDate,
  uniqueRosterEvents
} from "@/helpers/rosterSpan";

export const LEAVE_TYPES = [
  RESOURCE_SICK,
  RESOURCE_HOLIDAY,
  RESOURCE_COMPASSIONATE_LEAVE
];

const SHIFT_TYPES = [RESOURCE_WORK_HOURS, RESOURCE_BREAK, ...LEAVE_TYPES];

export function mondayOfWeek(isoDate) {
  const seed = isValidRosterDate(isoDate)
    ? isoDate
    : moment.utc().format("YYYY-MM-DD");
  const m = moment.utc(seed, "YYYY-MM-DD", true);
  const day = m.day();
  const offset = day === 0 ? -6 : 1 - day;
  return m.clone().add(offset, "days").format("YYYY-MM-DD");
}

export function addDaysIso(isoDate, days) {
  return moment
    .utc(isoDate, "YYYY-MM-DD", true)
    .add(days, "days")
    .format("YYYY-MM-DD");
}

export function weekDates(weekStart) {
  return Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i));
}

export { coerceEventMoment };

export function eventDate(event) {
  return eventStartDate(event);
}

export function eventTimeLabel(iso) {
  if (!iso) return "";
  if (typeof iso === "string" && isValidRosterTime(iso)) {
    const parts = iso.split(":");
    const hour = parts[0].length === 1 ? `0${parts[0]}` : parts[0];
    return `${hour}:${parts[1]}`;
  }
  if (typeof iso === "string" && iso.length >= 16 && iso[10] === "T") {
    return iso.slice(11, 16);
  }
  const m = coerceEventMoment(iso);
  return m ? m.format("HH:mm") : "";
}

export function isValidShiftRange(startTime, endTime) {
  if (!isValidRosterTime(startTime) || !isValidRosterTime(endTime)) {
    return false;
  }
  const startParts = startTime.split(":").map(Number);
  const endParts = endTime.split(":").map(Number);
  if (
    startParts[0] > 23 ||
    endParts[0] > 23 ||
    startParts[1] > 59 ||
    endParts[1] > 59
  ) {
    return false;
  }
  return startParts[0] * 60 + startParts[1] < endParts[0] * 60 + endParts[1];
}

export function shiftIsoByDays(iso, days) {
  if (!iso) return iso;
  if (iso instanceof Date) {
    return moment.utc(iso).add(days, "days").format();
  }
  return moment.utc(iso).add(days, "days").format();
}

export function isRosterShift(event) {
  return !!(event && SHIFT_TYPES.includes(event.type));
}

export function eventsForCell(events, resourceId, date) {
  return (events || []).filter(
    event =>
      isRosterShift(event) &&
      event.resourceId === resourceId &&
      eventOccupiesDate(event, date)
  );
}

export function formatSpanRange(start, end) {
  if (!isValidRosterDate(start)) return "";
  const a = moment.utc(start, "YYYY-MM-DD", true);
  if (!isValidRosterDate(end) || end === start) {
    return a.format("dddd D MMMM");
  }
  const b = moment.utc(end, "YYYY-MM-DD", true);
  if (a.month() === b.month() && a.year() === b.year()) {
    return `${a.format("dddd D")} – ${b.format("dddd D MMMM")}`;
  }
  if (a.year() === b.year()) {
    return `${a.format("dddd D MMMM")} – ${b.format("dddd D MMMM")}`;
  }
  return `${a.format("dddd D MMMM YYYY")} – ${b.format("dddd D MMMM YYYY")}`;
}

export function formatSpanShort(start, end) {
  if (!isValidRosterDate(start)) return "";
  const a = moment.utc(start, "YYYY-MM-DD", true);
  if (!isValidRosterDate(end) || end === start) {
    return a.format("ddd D");
  }
  const b = moment.utc(end, "YYYY-MM-DD", true);
  if (mondayOfWeek(start) === mondayOfWeek(end)) {
    return `${a.format("ddd")}–${b.format("ddd")}`;
  }
  if (a.month() === b.month() && a.year() === b.year()) {
    return `${a.format("D")}–${b.format("D MMM")}`;
  }
  if (a.year() === b.year()) {
    return `${a.format("D MMM")} – ${b.format("D MMM")}`;
  }
  return `${a.format("D MMM YYYY")} – ${b.format("D MMM YYYY")}`;
}

function spanMeta(events) {
  const dates = [];
  (events || []).forEach(event => {
    eventOccupiedDates(event).forEach(date => {
      if (dates.indexOf(date) === -1) dates.push(date);
    });
  });
  dates.sort();
  if (dates.length <= 1) {
    return { spanLabel: null, rangeLabel: null, classic: false };
  }
  return {
    spanLabel: formatSpanShort(dates[0], dates[dates.length - 1]),
    rangeLabel: formatSpanRange(dates[0], dates[dates.length - 1]),
    classic: true
  };
}

export function summarizeCell(events, date) {
  const list = events || [];
  const span = spanMeta(list);
  const leave = list.find(event => LEAVE_TYPES.includes(event.type));
  if (leave) {
    const sick = leave.type === RESOURCE_SICK;
    const compassionate = leave.type === RESOURCE_COMPASSIONATE_LEAVE;
    return {
      kind: sick ? "sick" : "holiday",
      label: sick ? "Sick" : compassionate ? "Leave" : "Away",
      start: null,
      end: null,
      events: list,
      ...span
    };
  }

  const work = list
    .filter(event => event.type === RESOURCE_WORK_HOURS)
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  if (work.length) {
    const firstTimes =
      (date && clippedTimeLabels(work[0], date)) || {
        start: eventTimeLabel(work[0].start),
        end: eventTimeLabel(work[0].end)
      };
    const lastTimes =
      (date && clippedTimeLabels(work[work.length - 1], date)) || {
        start: eventTimeLabel(work[work.length - 1].start),
        end: eventTimeLabel(work[work.length - 1].end)
      };
    const start = firstTimes.start;
    const end = lastTimes.end;
    const breakEvent = list.find(event => event.type === RESOURCE_BREAK);
    const breakTimes =
      breakEvent && date
        ? clippedTimeLabels(breakEvent, date)
        : breakEvent
          ? {
              start: eventTimeLabel(breakEvent.start),
              end: eventTimeLabel(breakEvent.end)
            }
          : null;
    return {
      kind: "work",
      label: `${start}–${end}`,
      start,
      end,
      breakLabel: breakTimes ? `${breakTimes.start}–${breakTimes.end}` : null,
      events: list,
      ...span
    };
  }

  return {
    kind: "off",
    label: "Off",
    start: null,
    end: null,
    events: list,
    spanLabel: null,
    rangeLabel: null,
    classic: false
  };
}

export function minutesBetween(startIso, endIso) {
  const start = moment.utc(startIso);
  const end = moment.utc(endIso);
  if (!start.isValid() || !end.isValid()) return 0;
  return Math.max(0, end.diff(start, "minutes"));
}

export function weekHoursForResource(events, resourceId, dates) {
  let minutes = 0;
  dates.forEach(date => {
    const cell = eventsForCell(events, resourceId, date);
    cell.forEach(event => {
      if (event.type === RESOURCE_WORK_HOURS) {
        minutes += minutesOnDate(event, date);
      }
      if (event.type === RESOURCE_BREAK) {
        minutes -= minutesOnDate(event, date);
      }
    });
  });
  return Math.max(0, minutes) / 60;
}

export function shopHoursForDate(shop, date) {
  const day = weekdayKeyFromIsoDate(date);
  const open = shop && shop.daysOpen && shop.daysOpen[day];
  const start = shop && shop.openingTimes && shop.openingTimes[day];
  const end = shop && shop.closingTimes && shop.closingTimes[day];
  if (!open || !isValidRosterTime(start) || !isValidRosterTime(end)) {
    return null;
  }
  return { start, end, day };
}

export function dayCoverage(shop, events, resourceIds, date) {
  const hours = shopHoursForDate(shop, date);
  if (!hours) {
    return { open: false, covered: true, working: 0 };
  }
  const working = (resourceIds || []).filter(id => {
    return eventsForCell(events, id, date).some(
      event => event.type === RESOURCE_WORK_HOURS
    );
  }).length;
  return { open: true, covered: working > 0, working };
}

export function formatWeekHeading(weekStart) {
  const start = moment.utc(weekStart, "YYYY-MM-DD", true);
  const end = start.clone().add(6, "days");
  if (start.month() === end.month()) {
    return `${start.format("D")}–${end.format("D MMM YYYY")}`;
  }
  return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
}

export function formatWeekDisplay(weekStart, todayIso) {
  const start = moment.utc(weekStart, "YYYY-MM-DD", true);
  const end = start.clone().add(6, "days");
  const today = moment.utc(
    isValidRosterDate(todayIso) ? todayIso : moment.utc().format("YYYY-MM-DD"),
    "YYYY-MM-DD",
    true
  );
  const showYear = start.year() !== today.year() || end.year() !== today.year();
  const year = showYear ? ` ${end.format("YYYY")}` : "";
  if (start.month() === end.month()) {
    return `${start.format("D")}–${end.format("D MMMM")}${year}`;
  }
  return `${start.format("D MMM")} – ${end.format("D MMM")}${year}`;
}

export function staffInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function formatHoursLabel(hours) {
  const value = Number(hours);
  if (!value) return "0h";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1)}h`;
}

export function isWeekendIso(date) {
  if (!isValidRosterDate(date)) return false;
  const day = moment.utc(date, "YYYY-MM-DD", true).day();
  return day === 0 || day === 6;
}

export function weekInsight({ shop, events, resourceIds, week, today }) {
  const ids = resourceIds || [];
  const dates = week || [];
  let gaps = 0;
  let openDays = 0;
  let workingToday = 0;

  dates.forEach(date => {
    const cover = dayCoverage(shop, events, ids, date);
    if (cover.open) {
      openDays += 1;
      if (!cover.covered) gaps += 1;
    }
    if (date === today) workingToday = cover.working;
  });

  const hours = ids.reduce((sum, id) => {
    return sum + weekHoursForResource(events, id, dates);
  }, 0);

  return {
    gaps,
    openDays,
    workingToday,
    hours: Math.round(hours * 10) / 10,
    staffCount: ids.length
  };
}

export function formatInsightLine(insight, options) {
  const data = insight || {};
  const isThisWeek = !!(options && options.isThisWeek);
  const parts = [];

  if (data.openDays > 0 && data.gaps === 0) {
    parts.push("Covered");
  } else if (data.gaps === 1) {
    parts.push("1 day to cover");
  } else if (data.gaps > 1) {
    parts.push(`${data.gaps} days to cover`);
  } else {
    parts.push("Closed this week");
  }

  if (isThisWeek) {
    parts.push(
      data.workingToday === 1 ? "1 on today" : `${data.workingToday} on today`
    );
  }

  parts.push(formatHoursLabel(data.hours));
  return parts.join(" · ");
}

export function shortDayLabel(date) {
  return moment.utc(date, "YYYY-MM-DD", true).format("ddd");
}

export function monthDayLabel(date) {
  return moment.utc(date, "YYYY-MM-DD", true).format("D");
}

export function formatDayShort(date) {
  if (!isValidRosterDate(date)) return "";
  return moment.utc(date, "YYYY-MM-DD", true).format("ddd D MMM");
}

export function previewRowsFromCreates(creates, employees) {
  const groups = {};
  uniqueRosterEvents(creates).forEach(event => {
    const dates = eventOccupiedDates(event);
    const useDates = dates.length
      ? dates
      : [eventDate(event)].filter(Boolean);
    useDates.forEach(date => {
      if (!date || !event.resourceId) return;
      const key = `${event.resourceId}|${date}`;
      if (!groups[key]) {
        groups[key] = { resourceId: event.resourceId, date, events: [] };
      }
      groups[key].events.push(event);
    });
  });

  const nameOf = id => {
    const person = (employees || []).find(item => item && item._id === id);
    return person && person.name ? person.name : "Staff";
  };

  return Object.keys(groups)
    .map(key => {
      const group = groups[key];
      const summary = summarizeCell(group.events);
      return {
        resourceId: group.resourceId,
        name: nameOf(group.resourceId),
        date: group.date,
        dayLabel: formatDayShort(group.date),
        kind: summary.kind,
        label: summary.label
      };
    })
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return String(a.name).localeCompare(String(b.name));
    });
}

function leaveDraft(event, destDate) {
  return {
    type: event.type,
    resourceId: event.resourceId,
    allDay: false,
    start: `${destDate}T00:00:00Z`,
    end: `${destDate}T23:59:00Z`
  };
}

/**
 * Copy a source event onto destDate as a v2-native single-day event.
 * Classic multi-day leave becomes one 00:00–23:59 day so paste/copy
 * does not keep spreading the old spanning document.
 */
export function materializeEventForDate(event, destDate, sourceDate) {
  if (!event || !isValidRosterDate(destDate)) return null;
  if (LEAVE_TYPES.includes(event.type)) {
    return leaveDraft(event, destDate);
  }
  const clipDate = isValidRosterDate(sourceDate) ? sourceDate : destDate;
  const times = clippedTimeLabels(event, clipDate);
  if (!times) return null;
  let startTime = times.start;
  let endTime = times.end;
  if (!isValidShiftRange(startTime, endTime)) {
    if (startTime === "00:00" && (endTime === "00:00" || endTime === "23:59")) {
      endTime = "23:59";
    } else {
      return null;
    }
  }
  if (!isValidShiftRange(startTime, endTime)) return null;
  return {
    type: event.type,
    resourceId: event.resourceId,
    allDay: false,
    start: `${destDate}T${startTime}:00Z`,
    end: `${destDate}T${endTime}:00Z`
  };
}

export function planClearDay(cellEvents) {
  return {
    deletes: (cellEvents || []).filter(isRosterShift),
    creates: []
  };
}

/**
 * Mark every staff cell in the week Off by deleting work, lunch, sick and away.
 * Bookings are left alone. Empty cells are skipped.
 */
export function planClearWeek({ events, resourceIds, week }) {
  const deletes = [];
  let cleared = 0;

  (resourceIds || []).forEach(resourceId => {
    (week || []).forEach(date => {
      const cell = eventsForCell(events, resourceId, date);
      if (!cell.length) return;
      deletes.push(...cell);
      cleared += 1;
    });
  });

  return { deletes: uniqueRosterEvents(deletes), creates: [], cleared };
}

export function planSetWork({
  cellEvents,
  employee,
  date,
  startTime,
  endTime
}) {
  if (!isValidShiftRange(startTime, endTime)) {
    return { deletes: [], creates: [], invalid: true };
  }
  const custom = {
    ...employee,
    rosterDefaultWorkDays: {
      ...(employee.rosterDefaultWorkDays || {}),
      [weekdayKeyFromIsoDate(date)]: true
    },
    rosterDefaultWorkStartTimes: {
      ...(employee.rosterDefaultWorkStartTimes || {}),
      [weekdayKeyFromIsoDate(date)]: startTime
    },
    rosterDefaultWorkEndTimes: {
      ...(employee.rosterDefaultWorkEndTimes || {}),
      [weekdayKeyFromIsoDate(date)]: endTime
    }
  };
  return {
    deletes: (cellEvents || []).filter(isRosterShift),
    creates: buildRosterEventsForDate(date, custom, employee._id),
    invalid: false
  };
}

export function planSetLeave({ cellEvents, employee, date, type }) {
  return {
    deletes: (cellEvents || []).filter(isRosterShift),
    creates: [
      {
        type,
        resourceId: employee._id,
        storeId: employee.storeId || "",
        tenantId: employee.tenantId || "",
        start: `${date}T00:00:00Z`,
        end: `${date}T23:59:00Z`,
        allDay: false
      }
    ]
  };
}

export function planCopyPreviousWeek({
  events,
  resourceIds,
  currentWeek,
  previousWeek
}) {
  const deletes = [];
  const creates = [];
  let copied = 0;
  let skipped = 0;

  resourceIds.forEach(resourceId => {
    currentWeek.forEach((date, index) => {
      const sourceDate = previousWeek[index];
      const source = eventsForCell(events, resourceId, sourceDate);
      const current = eventsForCell(events, resourceId, date);
      if (current.length) {
        if (source.length) skipped += 1;
        return;
      }
      if (!source.length) return;
      source.forEach(event => {
        const draft = materializeEventForDate(event, date, sourceDate);
        if (draft) creates.push(draft);
      });
      copied += 1;
    });
  });

  return { deletes, creates, copied, skipped };
}

export function snapshotWeek({ events, resourceIds, week }) {
  const dates = week || [];
  const cells = [];

  (resourceIds || []).forEach(resourceId => {
    dates.forEach((date, dayIndex) => {
      const source = eventsForCell(events, resourceId, date);
      if (!source.length) return;
      const drafts = source
        .map(event => {
          const draft = materializeEventForDate(event, date, date);
          if (!draft) return null;
          return {
            type: draft.type,
            start: eventTimeLabel(draft.start),
            end: eventTimeLabel(draft.end),
            allDay: !!draft.allDay
          };
        })
        .filter(Boolean);
      if (!drafts.length) return;
      cells.push({
        resourceId,
        dayIndex,
        events: drafts
      });
    });
  });

  return {
    weekStart: dates[0] || null,
    cells,
    copied: cells.length
  };
}

export function planPasteWeek({ snapshot, events, destWeek, resourceIds }) {
  const deletes = [];
  const creates = [];
  let pasted = 0;
  let skipped = 0;
  const dest = destWeek || [];
  const cells = (snapshot && snapshot.cells) || [];
  const allowed = resourceIds ? new Set(resourceIds) : null;

  if (snapshot && snapshot.weekStart && dest[0] === snapshot.weekStart) {
    return { deletes, creates, pasted: 0, skipped: 0, sameWeek: true };
  }

  cells.forEach(cell => {
    const date = dest[cell.dayIndex];
    if (!isValidRosterDate(date) || !cell.resourceId) return;
    if (allowed && !allowed.has(cell.resourceId)) return;
    const current = eventsForCell(events, cell.resourceId, date);
    const drafts = (cell.events || [])
      .map(event => {
        if (!eventTimeLabel(event.start) || !eventTimeLabel(event.end)) return null;
        return {
          type: event.type,
          resourceId: cell.resourceId,
          allDay: !!event.allDay,
          start: `${date}T${eventTimeLabel(event.start)}:00Z`,
          end: `${date}T${eventTimeLabel(event.end)}:00Z`
        };
      })
      .filter(Boolean);
    if (!drafts.length) return;
    if (current.length) {
      skipped += 1;
      return;
    }
    creates.push(...drafts);
    pasted += 1;
  });

  return { deletes, creates, pasted, skipped, sameWeek: false };
}

export function planFillDefaults({ events, employees, shop, week }) {
  const deletes = [];
  const creates = [];
  let filled = 0;
  let skipped = 0;

  employees.forEach(employee => {
    week.forEach(date => {
      const current = eventsForCell(events, employee._id, date);
      if (current.length) {
        skipped += 1;
        return;
      }
      if (!shopHoursForDate(shop, date) && !(employee.rosterDefaultWorkDays || {})[weekdayKeyFromIsoDate(date)]) {
        return;
      }
      const drafts = buildRosterEventsForDate(date, employee, employee._id);
      if (!drafts.length) return;
      creates.push(...drafts);
      filled += 1;
    });
  });

  return { deletes, creates, filled, skipped };
}

export function classicSpanConflict(cellEvents, date) {
  const spanning = uniqueRosterEvents(cellEvents).filter(isMultiDayEvent);
  if (!spanning.length) return null;
  const dateSet = {};
  spanning.forEach(event => {
    eventOccupiedDates(event).forEach(day => {
      dateSet[day] = true;
    });
  });
  const dates = Object.keys(dateSet).sort();
  if (!dates.length) return null;
  return {
    events: spanning,
    dates,
    start: dates[0],
    end: dates[dates.length - 1],
    rangeLabel: formatSpanRange(dates[0], dates[dates.length - 1]),
    shortLabel: formatSpanShort(dates[0], dates[dates.length - 1]),
    date
  };
}

export function formatClassicSpanWarning({ rangeLabel, date, nextLabel }) {
  const day = formatDayShort(date) || "this day";
  const head = `This was created on the classic calendar as one event covering ${rangeLabel}. Changing ${day} will remove the whole period, not just that day.`;
  if (nextLabel) {
    return `${head} ${day} will then be ${nextLabel}. The other days will be Off.`;
  }
  return `${head} Every day in that period will be Off.`;
}

export function spanningOutsideWeek({ events, resourceIds, week }) {
  const weekSet = {};
  (week || []).forEach(date => {
    weekSet[date] = true;
  });
  const allowed = {};
  (resourceIds || []).forEach(id => {
    allowed[id] = true;
  });
  const extra = [];
  uniqueRosterEvents(events).forEach(event => {
    if (!isRosterShift(event)) return;
    if (event.resourceId && resourceIds && !allowed[event.resourceId]) return;
    if (!isMultiDayEvent(event)) return;
    const dates = eventOccupiedDates(event);
    const inWeek = dates.some(day => weekSet[day]);
    const out = dates.filter(day => !weekSet[day]);
    if (!inWeek || !out.length) return;
    extra.push({ event, dates: out, allDates: dates });
  });
  return extra;
}

export function rosterableEmployees(employees) {
  return (employees || []).filter(
    employee => employee && employee._id && employee._id !== "Any"
  );
}

export { ROSTER_DAYS };
